import cn from 'classnames';
import React, { ReactNode, useCallback, useMemo } from 'react';

import '../styles/style.scss';

import { AppContext, AppContextProps } from 'contexts/App/AppContext';
import I18n from 'hooks/useI18n';
import { useRouter } from 'hooks/useRouter';
import useTranslateSessionLocale from 'hooks/useTranslateSessionLocale';
import AgeModal from 'modules/modals/AgeModal/AgeModal';
import BrowserModal from 'modules/modals/BrowserModal';
import NotificationModal from 'modules/modals/NotificationModal';
import { onCloseConfirmModal, updateCommonStateReducerSuccess } from 'redhotpie-shared-lib/actions/commonStateAction';
import { ELEMENT_ID } from 'redhotpie-shared-lib/constants/constants';
import { getClassByTheme, getFontClassByFont, phoneDetect } from 'redhotpie-shared-lib/functions/utils';
import { useDispatch, useSelector } from 'redhotpie-shared-lib/hooks/useRedux';
import { IConfirmModal } from 'redhotpie-shared-lib/interfaces/IInitialState';
import {
  selectAgeModalInfo,
  selectBrowserModalCustomContainer,
  selectConfirmModal,
  selectHeightMax,
  selectIsFeedback,
  selectIsHideLoadingBrowserModal,
  selectIsShowBrowserModal,
  selectUrlBrowserModal,
  selectWidthMax,
} from 'redhotpie-shared-lib/selectors/commonStateSelector';
import { getIsLogOut, getMessengerSetting, getUserInfo } from 'redhotpie-shared-lib/selectors/loginSelector';

/**
 * Lightweight application shell for the registration site.
 *
 * The full RedHotPie web app is driven by components/App.tsx, which wires up
 * messenger, XMPP, video calls, feeds, payments and dozens of modals. The
 * registration site only has four routes (home, quick registration, short
 * link, not found), so it gets its own shell that provides just what those
 * routes rely on:
 *
 *  - AppContext with `translateTextStatic` (the only context member the
 *    registration steps read) plus inert defaults for the rest,
 *  - the confirm/notification modal, the in-app browser modal and the age
 *    picker modal, all driven by commonStateReducer exactly like App.tsx,
 *  - the same root wrapper element and theme classes so shared styles apply.
 *
 * Anything else App.tsx does is intentionally left out, which keeps the
 * whole messenger/profile code base out of this bundle.
 */
const noop = () => {};

function RegistrationApp({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const { history } = useRouter();
  const sessionLocale = useTranslateSessionLocale();

  const userInfo = useSelector(getUserInfo);
  const messengerSetting = useSelector(getMessengerSetting);
  const isLogout = useSelector(getIsLogOut);
  const confirmModalSelector = useSelector(selectConfirmModal);
  const isShowBrowserModal = useSelector(selectIsShowBrowserModal);
  const urlBrowserModal = useSelector(selectUrlBrowserModal);
  const isHideLoadingBrowserModal = useSelector(selectIsHideLoadingBrowserModal);
  const isFeedback = useSelector(selectIsFeedback);
  const widthMax = useSelector(selectWidthMax);
  const heightMax = useSelector(selectHeightMax);
  const browserModalCustomContainer = useSelector(selectBrowserModalCustomContainer);
  const { isShow: isShowAgeModal, ageInfo, onClickDone, idTabSelected } = useSelector(selectAgeModalInfo);

  const isMobile = phoneDetect();
  const isInIframe = window.location !== window.parent.location;
  const fontSize = messengerSetting?.theme?.fontSize;
  const theme = messengerSetting?.theme?.themeType;

  const confirmModal = useMemo(() => confirmModalSelector.asMutable() as IConfirmModal, [confirmModalSelector]);

  const translateTextStatic = useCallback(
    (text: string, params = {}) =>
      I18n.t(text, {
        ...params,
        ...(sessionLocale ? { locale: sessionLocale } : {}),
      }),
    [sessionLocale]
  );

  const onCancelNotificationModal = () => {
    const wrapperMobile = document.getElementById('wrapper-mobile');
    if (wrapperMobile) {
      wrapperMobile.style.overflow = '';
      wrapperMobile.style.position = '';
      if (confirmModal.wrapperPosition !== 0) {
        window.scrollTo(0, confirmModal.wrapperPosition);
      }
    }
    if (typeof confirmModal?.onCancelAction === 'function') {
      confirmModal.onCancelAction();
    }
  };

  let onClickNotification: any = noop;
  if (typeof confirmModal.onOkAction === 'function') {
    onClickNotification = (params: any) => confirmModal.onOkAction(onCancelNotificationModal, confirmModal.callbackRunAnimation, params);
  }

  const closeBrowserModal = () =>
    dispatch(
      updateCommonStateReducerSuccess({
        isShowBrowserModal: false,
        urlBrowserModal: '',
        isHideLoadingBrowserModal: false,
        widthMax: null,
        heightMax: null,
        browserModalCustomContainer: '',
      })
    );

  const closeAgeModal = () =>
    dispatch(
      updateCommonStateReducerSuccess({
        ageModalInfo: { isShow: false, onClickDone: null, ageInfo: null, idTabSelected: '' },
      })
    );

  const contextValue: AppContextProps = {
    timeLeft: 0,
    timeLeftString: '',
    isBoosted: false,
    setShowBoostSuccess: noop,
    handleShowBoostProfileModal: noop,
    handleShowBoostConversationModal: noop,
    handleShowGetMoreProduct: noop,
    handleCheckBoostProfile: () => false,
    isOutAU: false,
    currentStoreCredits: null,
    userInfo,
    history,
    handleOpenRHPCoins: noop,
    onCloseProfileMatchModal: noop,
    handleOpenCommentModal: noop,
    handleOpenLikeModal: noop,
    handleOpenProfileCommon: noop,
    handleOpenCommonPhotoModal: noop,
    handleAppGoBack: () => history.goBack(),
    handleOpenEventDetail: noop,
    handleGetVerified: noop,
    vericalScrollTo: (y: number) => window.scrollTo(0, y),
    translateTextStatic,
  };

  return (
    <AppContext.Provider value={contextValue}>
      <div
        id={ELEMENT_ID.MAIN_APP}
        className={cn('rhp-app-messenger', {
          [getFontClassByFont(fontSize)]: fontSize,
          [getClassByTheme(theme)]: theme,
        })}>
        {children}
        <NotificationModal
          showModal={
            (!!confirmModal.messageConfirm || !!confirmModal.messageConfirmExternal || !!confirmModal.messageConfirmExternal2) && !isLogout
          }
          headerText={confirmModal.headerText}
          content={confirmModal.messageConfirm}
          contentSecond={confirmModal.messageConfirmExternal}
          contentThird={confirmModal.messageConfirmExternal2}
          okText={confirmModal.okText ? confirmModal.okText : I18n.t('Btn.OK')}
          cancelText={confirmModal.cancelText ? confirmModal.cancelText : I18n.t('Btn.Cancel')}
          extraFooter={confirmModal.extraFooter}
          onActionClick={onClickNotification}
          onClose={() => dispatch(onCloseConfirmModal())}
          onCancelClick={() => {
            if (typeof confirmModal.onCancelAction === 'function') {
              confirmModal.onCancelAction(onCancelNotificationModal);
            } else {
              onCancelNotificationModal();
            }
          }}
          zIndexModal={confirmModal.customZIndex || 40}
          customClassButton={confirmModal.customClassButton}
          customClassOk={confirmModal.customClassOk}
          customClassCancle={confirmModal.customClassCancle}
          classNames={confirmModal.classNames}
          disableCancel={confirmModal.disableCancel}
          wrapperPosition={confirmModal.wrapperPosition}
          disableClickOverlay={confirmModal.disableClickOverlay}
          noClearCancelAction={confirmModal.noClearCancelAction || false}
          keepModalWhenClickCancel={confirmModal.keepModalWhenClickCancel || false}
          isMoreEndGroupChat={confirmModal.isMoreEndGroupChat || false}
          disableEscClose={confirmModal.disableEscClose}
          disableBackBrowserCloseModal={confirmModal.disableBackBrowserCloseModal}
          isBottomModal={confirmModal.isBottomModal}
          isOptionModal={confirmModal.isOptionModal}
          isOverlayBlack={confirmModal.isOverlayBlack}
        />
        <BrowserModal
          isBrowserModal
          zIndexModal={40}
          isBottomModal={isMobile}
          showModal={isShowBrowserModal}
          onClose={closeBrowserModal}
          url={urlBrowserModal}
          onCallbackAfterOpen={noop}
          isHideLoadingBrowserModal={isHideLoadingBrowserModal}
          widthMax500={isFeedback}
          widthMax={isFeedback ? 500 : widthMax}
          heightMax={heightMax}
          isRenderCustomBodyContainer
          customClassContainer={browserModalCustomContainer}
        />
        {isShowAgeModal && (
          <AgeModal
            showModal={isShowAgeModal}
            onClose={closeAgeModal}
            isNotificationModal={isMobile || isInIframe}
            headerText={!isMobile && !isInIframe ? I18n.t('LookingToMeetScreen.Age') : undefined}
            isOptionModal={isMobile || isInIframe}
            widthMax={!isMobile && !isInIframe ? 400 : undefined}
            isShowCloseInside={!isMobile && !isInIframe}
            ageInfo={ageInfo}
            onClickDone={onClickDone}
            idTabSelected={idTabSelected}
            fullScreen={isMobile || isInIframe}
            isNoShowIconClose={isMobile || isInIframe}
          />
        )}
      </div>
    </AppContext.Provider>
  );
}

export default RegistrationApp;
